/**
 * NAKŁADKA PWA — rejestracja service workera i przechwycenie zaproszenia do instalacji.
 * Brief właściciela 2026-09-02, poz. 6.
 *
 * DWIE RZECZY, KTÓRE MUSZĄ SIĘ WYDARZYĆ PRZED POKAZANIEM PRZYCISKU:
 *  1. Przeglądarka musi mieć zarejestrowany service worker z obsługą `fetch` — bez tego Chrome
 *     nigdy nie wyśle zdarzenia `beforeinstallprompt` i przycisk byłby atrapą.
 *  2. To zdarzenie trzeba PRZECHWYCIĆ MOŻLIWIE WCZEŚNIE i zapamiętać. Przeglądarka wysyła je raz,
 *     zwykle zanim React zdąży zamontować pulpit; komponent nasłuchujący dopiero po zamontowaniu
 *     przegapiłby je i pokazywałby instrukcję ręczną osobie, która mogłaby kliknąć jeden przycisk.
 *     Dlatego nasłuch startuje z `index.js`, a wynik trafia do modułowego pudełka poniżej.
 */

let zapamietaneZdarzenie = null;
const sluchacze = new Set();

const powiadom = () => { sluchacze.forEach((fn) => { try { fn(); } catch { /* jeden zły słuchacz nie psuje reszty */ } }); };

/** Czy aplikacja działa już jako zainstalowane okno (a nie zakładka przeglądarki). */
export const czyUruchomionaJakoAplikacja = () => {
    if (typeof window === "undefined") return false;
    const standalone = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
    // `navigator.standalone` to wariant iOS — nie ma go w Chrome, więc sprawdzamy oba.
    return Boolean(standalone || window.navigator?.standalone);
};

export const dostepnaInstalacja = () => zapamietaneZdarzenie !== null;

export const subskrybujPwa = (fn) => {
    sluchacze.add(fn);
    return () => sluchacze.delete(fn);
};

/**
 * Pokazuje systemowe okno instalacji. Zwraca "accepted" / "dismissed" / "unavailable".
 * MUSI być wołane z gestu użytkownika (kliknięcia) — inaczej przeglądarka je odrzuci.
 */
export const uruchomInstalacje = async () => {
    if (!zapamietaneZdarzenie) return "unavailable";
    const zdarzenie = zapamietaneZdarzenie;
    try {
        zdarzenie.prompt();
        const { outcome } = await zdarzenie.userChoice;
        // Zaproszenie jest jednorazowe: po pokazaniu nie da się go użyć drugi raz.
        // Trzymanie go dalej dałoby przycisk, który przy drugim kliknięciu nic nie robi.
        zapamietaneZdarzenie = null;
        powiadom();
        return outcome;
    } catch {
        zapamietaneZdarzenie = null;
        powiadom();
        return "dismissed";
    }
};

/** Wpinane raz, przy starcie aplikacji. */
export const zainicjujPwa = () => {
    if (typeof window === "undefined") return;

    window.addEventListener("beforeinstallprompt", (e) => {
        // Bez `preventDefault` przeglądarka pokazuje własny pasek — właściciel chce przycisku
        // na pulpicie, w miejscu, o którym można powiedzieć pracownikowi przez telefon.
        e.preventDefault();
        zapamietaneZdarzenie = e;
        powiadom();
    });

    window.addEventListener("appinstalled", () => {
        zapamietaneZdarzenie = null;
        powiadom();
    });

    if (!("serviceWorker" in navigator)) return;

    // Rejestrujemy PO `load`, żeby nie konkurować o łącze z pierwszym renderem aplikacji.
    window.addEventListener("load", () => {
        navigator.serviceWorker.register("/service-worker.js")
            .then((rejestracja) => {
                // Wdrożenia są częste: przy każdym wejściu prosimy przeglądarkę o sprawdzenie,
                // czy worker się zmienił. Sam worker niczego nie cachuje, więc to tanie.
                rejestracja.update?.();
            })
            .catch(() => {
                // Brak HTTPS, wyłączone service workery w polityce firmowej albo tryb prywatny.
                // To nie jest awaria aplikacji — po prostu instalacja nie będzie dostępna,
                // a ekran pulpitu pokaże wtedy instrukcję ręczną.
            });
    });
};
